import { useEffect, useState, useMemo, useCallback } from "react";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import BriefReviewModal from "@/components/BriefReviewModal";
import {
  getBoroughs, bulkDiscover, bulkExportUrl,
  listLeadJobs, createLeadJob, deleteLeadJob, runLeadJobNow,
  createDemo, createProspect, verifyRawWebsite,
} from "@/lib/api";
import { BULK } from "@/constants/testIds";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Download, AlertCircle, ExternalLink, Loader2, Trash2, Play, Sparkles, Plus, Check, X, Search } from "lucide-react";

const MINIMUM_BUILD_SCORE = 50;

export default function BulkDiscover() {
  const navigate = useNavigate();
  const [boroughs, setBoroughs] = useState([]);
  const [tradePresets, setTradePresets] = useState([]);
  const [tradeCategories, setTradeCategories] = useState({});
  const [borough, setBorough] = useState("Hackney");
  const [picked, setPicked] = useState(new Set(["plumber", "electrician", "gas engineer"]));
  const [running, setRunning] = useState(false);
  const [session, setSession] = useState(null);
  const [filterNoSite, setFilterNoSite] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [buildingFor, setBuildingFor] = useState(null);
  const [verifyingIdx, setVerifyingIdx] = useState(null);
  const [reviewDemo, setReviewDemo] = useState(null);
  const [addedToPipeline, setAddedToPipeline] = useState(new Set());
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleName, setScheduleName] = useState("");
  const [scheduleHour, setScheduleHour] = useState(3);

  const refreshJobs = useCallback(() => listLeadJobs().then(setJobs).catch(() => {}), []);

  useEffect(() => {
    getBoroughs().then((d) => {
      setBoroughs(d.boroughs || []);
      setTradePresets(d.trades || []);
      setTradeCategories(d.categories || {});
    }).catch(() => toast.error("Could not load boroughs"));
    refreshJobs();
  }, [refreshJobs]);

  const toggle = (t) => {
    const next = new Set(picked);
    next.has(t) ? next.delete(t) : next.add(t);
    setPicked(next);
  };

  const run = async () => {
    if (picked.size === 0) { toast.error("Pick at least one trade"); return; }
    setRunning(true);
    setSession(null);
    try {
      const data = await bulkDiscover(borough, Array.from(picked));
      setSession(data);
      toast.success(`${data.total} candidates surfaced in ${borough}`);
    } catch (e) {
      if (e.code === "ECONNABORTED") {
        toast.error(`Trawl of ${picked.size} trades timed out — try fewer trades at once, or narrower categories.`);
      } else {
        const detail = e?.response?.data?.detail;
        const message = typeof detail === "string" ? detail
          : Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join("; ")
          : e?.message || "unknown error";
        toast.error("Bulk trawl failed: " + message);
      }
    } finally {
      setRunning(false);
    }
  };

  // Google Places' self-reported websiteUri (what has_website/website are built from) is often
  // empty even when a business has a real live site — this runs a live web-search check on a
  // single row on demand, instead of before every trawl result. Matched by title+address, not
  // array index — `filtered` can be a subset of `session.candidates` (NO SITE ONLY filter), so
  // an index into the rendered list doesn't line up with the index in the underlying array.
  const candidateKey = (r) => `${r.title}|${r.address || ""}`;

  const verifyCandidate = async (r) => {
    const key = candidateKey(r);
    setVerifyingIdx(key);
    try {
      const { website } = await verifyRawWebsite(r.title, r.address, session.borough);
      if (website) {
        setSession(prev => ({
          ...prev,
          candidates: prev.candidates.map(c => candidateKey(c) === key ? { ...c, website, has_website: true } : c),
        }));
        toast.warning(`Already has a site: ${website}`);
      } else {
        toast.success("No live website found — clear to build");
      }
    } catch (e) {
      toast.error("Verification failed: " + (e?.response?.data?.detail || e?.message || "unknown"));
    } finally {
      setVerifyingIdx(null);
    }
  };

  // Build now only generates the brief (cheap-ish) and stops — the expensive full site build
  // waits for BriefReviewModal's approval, so a wrong brief never burns a full HTML generation.
  const startBuild = async (r, i) => {
    setBuildingFor({ name: r.title, idx: i });
    try {
      const demo = await createDemo({
        name: r.title, trade: r.trade, borough: session.borough,
        address: r.address, phone: r.phone, rating: r.rating,
        reviews: r.reviews, website: r.website,
      });
      setReviewDemo({ demo, candidate: r });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const website = typeof detail === "object" ? detail?.website : null;
      if (website) {
        const key = candidateKey(r);
        setSession(prev => ({
          ...prev,
          candidates: prev.candidates.map(c => candidateKey(c) === key ? { ...c, website, has_website: true } : c),
        }));
        toast.warning(`Already has a site: ${website}`);
      } else {
        const message = typeof detail === "string" ? detail : detail?.message || e?.message || "unknown error";
        toast.error("Brief generation failed: " + message);
      }
    } finally {
      setBuildingFor(null);
    }
  };

  const handleBriefApproved = async (built, comment) => {
    const r = reviewDemo.candidate;
    const pKey = candidateKey(r);
    if (!addedToPipeline.has(pKey)) {
      await createProspect({
        name: r.title, trade: r.trade, borough: session.borough,
        address: r.address, phone: r.phone, rating: r.rating,
        reviews: r.reviews, website: r.website,
        demo_id: built.id, stage: "demo_built",
        ...(comment ? { notes: comment } : {}),
      });
      setAddedToPipeline(prev => new Set([...prev, pKey]));
    }
    setReviewDemo(null);
    navigate(`/demo/${built.id}`);
  };

  const filtered = useMemo(() => {
    if (!session) return [];
    return filterNoSite ? session.candidates.filter((c) => !c.has_website) : session.candidates;
  }, [session, filterNoSite]);

  const stats = useMemo(() => {
    if (!session) return null;
    const noSite = session.candidates.filter((c) => !c.has_website).length;
    const avgRating = (
      session.candidates.reduce((a, c) => a + (c.rating || 0), 0) / (session.candidates.length || 1)
    ).toFixed(2);
    const top = session.candidates[0]?.candidate_score || 0;
    return { noSite, avgRating, top };
  }, [session]);

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f4f4f5", color: "#111827" }}>
      <Nav />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] mb-4" style={{ color: "#FF3B30" }}>BULK · OVERNIGHT TRAWL</p>
        <h1 className="font-display text-5xl md:text-7xl tracking-tighter leading-[0.95] max-w-4xl">
          Scan an entire borough. Export the spreadsheet.
        </h1>
        <p className="mt-6 max-w-2xl text-lg" style={{ color: "#6b7280" }}>
          Pick a borough and trades. We hit Google Maps in parallel, surface every business with no website or a weak one,
          score them by reputation, and export the list.
        </p>

        {/* Controls */}
        <div className="mt-10" style={{ border: "1px solid #e5e7eb", backgroundColor: "#ffffff" }}>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 p-6" style={{ borderBottom: "1px solid #e5e7eb" }}>
            <div className="md:col-span-3">
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] block mb-2" style={{ color: "#9ca3af" }}>BOROUGH</label>
              <select
                data-testid={BULK.boroughSelect}
                value={borough}
                onChange={(e) => setBorough(e.target.value)}
                className="w-full px-3 py-3 text-base outline-none"
                style={{ backgroundColor: "#f9fafb", border: "1px solid #e5e7eb", color: "#111827" }}
              >
                {boroughs.map((b) => <option key={b}>{b}</option>)}
              </select>
            </div>
            <div className="md:col-span-9">
              <div className="flex items-center justify-between mb-3">
                <label className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: "#9ca3af" }}>
                  TRADES · {picked.size} SELECTED
                </label>
                <div className="flex gap-3">
                  <button
                    onClick={() => setPicked(new Set(tradePresets))}
                    className="font-mono text-[10px] uppercase tracking-[0.2em] hover:text-[#FF3B30] transition-colors"
                    style={{ color: "#9ca3af" }}
                  >
                    all
                  </button>
                  <button
                    onClick={() => setPicked(new Set())}
                    className="font-mono text-[10px] uppercase tracking-[0.2em] hover:text-[#FF3B30] transition-colors"
                    style={{ color: "#9ca3af" }}
                  >
                    clear
                  </button>
                </div>
              </div>
              {Object.keys(tradeCategories).length > 0 ? (
                <div className="space-y-4">
                  {Object.entries(tradeCategories).map(([cat, trades]) => (
                    <div key={cat}>
                      <div className="font-mono text-[9px] uppercase tracking-[0.2em] mb-2" style={{ color: "#d1d5db" }}>{cat}</div>
                      <div className="flex flex-wrap gap-2">
                        {trades.map((t) => {
                          const on = picked.has(t);
                          return (
                            <button
                              key={t}
                              data-testid={BULK.tradeChip(t.replace(/\s+/g, "-"))}
                              onClick={() => toggle(t)}
                              className="px-3 py-1.5 font-mono text-[11px] transition-colors"
                              style={on
                                ? { backgroundColor: "#FF3B30", border: "1px solid #FF3B30", color: "#ffffff" }
                                : { backgroundColor: "#ffffff", border: "1px solid #e5e7eb", color: "#6b7280" }
                              }
                            >
                              {t}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {tradePresets.map((t) => {
                    const on = picked.has(t);
                    return (
                      <button
                        key={t}
                        data-testid={BULK.tradeChip(t.replace(/\s+/g, "-"))}
                        onClick={() => toggle(t)}
                        className="px-3 py-1.5 font-mono text-[11px] transition-colors"
                        style={on
                          ? { backgroundColor: "#FF3B30", border: "1px solid #FF3B30", color: "#ffffff" }
                          : { backgroundColor: "#ffffff", border: "1px solid #e5e7eb", color: "#6b7280" }
                        }
                      >
                        {t}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <div className="p-6 flex flex-wrap items-center justify-between gap-4">
            <p className="font-mono text-xs" style={{ color: "#9ca3af" }}>
              ~{picked.size * 10} listings · scoring rating × reviews ÷ web weakness
            </p>
            <button
              data-testid={BULK.runBtn}
              onClick={run}
              disabled={running || picked.size === 0}
              className="bg-[#FF3B30] text-white px-8 py-3 font-display font-bold hover:bg-[#E6352B] disabled:opacity-50 flex items-center gap-2 transition-colors"
            >
              {running ? <><Loader2 className="w-4 h-4 animate-spin" /> Trawling…</> : <>Run bulk trawl →</>}
            </button>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-32">
        {!session && !running && (
          <div className="p-12 text-center font-mono text-sm" style={{ border: "1px solid #e5e7eb", color: "#9ca3af", backgroundColor: "#ffffff" }}>
            Pick borough + trades, then run the trawl.
          </div>
        )}

        {session && (
          <>
            {/* Stats / export */}
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
              <div className="flex flex-wrap items-baseline gap-6 sm:gap-8">
                <Stat v={session.total} l="candidates" />
                <Stat v={stats.noSite} l="no website" accent />
                <Stat v={stats.avgRating + "★"} l="avg. rating" />
                <Stat v={stats.top} l="top score" accent />
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-2 text-xs font-mono" style={{ color: "#6b7280" }}>
                  <input type="checkbox" checked={filterNoSite} onChange={(e) => setFilterNoSite(e.target.checked)} className="accent-[#FF3B30]" />
                  NO SITE ONLY
                </label>
                <a
                  data-testid={BULK.exportBtn}
                  href={bulkExportUrl(session.session_id)}
                  className="px-5 py-2.5 sm:px-6 sm:py-3 font-display font-bold flex items-center gap-2 transition-colors hover:bg-[#FF3B30] hover:text-white text-sm"
                  style={{ backgroundColor: "#111827", color: "#ffffff" }}
                >
                  <Download className="w-4 h-4" /> Export CSV
                </a>
              </div>
            </div>

            {/* Results — desktop table */}
            <div className="hidden md:block" style={{ border: "1px solid #e5e7eb", overflowX: "auto" }}>
              <div
                className="grid grid-cols-12 gap-4 px-6 py-3 font-mono text-[10px] uppercase tracking-[0.2em] min-w-[900px]"
                style={{ borderBottom: "1px solid #e5e7eb", backgroundColor: "#ffffff", color: "#9ca3af" }}
              >
                <div className="col-span-1">SCORE</div>
                <div className="col-span-3">BUSINESS</div>
                <div className="col-span-2">TRADE</div>
                <div className="col-span-2">ADDRESS</div>
                <div className="col-span-1 text-right">★</div>
                <div className="col-span-1 text-right">RVWS</div>
                <div className="col-span-1">SITE</div>
                <div className="col-span-1 text-right">DEMO</div>
              </div>
              {filtered.map((r, i) => {
                const rowKey = `${r.title}-${r.address || ""}-${r.phone || ""}`;
                return (
                  <div
                    key={rowKey}
                    data-testid={BULK.resultRow(i)}
                    className="grid grid-cols-12 gap-4 px-6 py-4 items-center min-w-[900px]"
                    style={{ borderBottom: "1px solid #f3f4f6", backgroundColor: "#ffffff" }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = "#f9fafb"}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = "#ffffff"}
                  >
                    <div className="col-span-1"><ScoreBadge score={r.candidate_score} /></div>
                    <div className="col-span-3">
                      <div className="font-display text-base leading-tight" style={{ color: "#111827" }}>{r.title}</div>
                      {r.phone && <div className="font-mono text-[10px] mt-1" style={{ color: "#9ca3af" }}>{r.phone}</div>}
                    </div>
                    <div className="col-span-2 font-mono text-[11px] uppercase tracking-wider" style={{ color: "#6b7280" }}>{r.trade}</div>
                    <div className="col-span-2 text-xs" style={{ color: "#6b7280" }}>{r.address || "—"}</div>
                    <div className="col-span-1 text-right font-mono text-sm" style={{ color: "#374151" }}>{r.rating ?? "—"}</div>
                    <div className="col-span-1 text-right font-mono text-sm" style={{ color: "#6b7280" }}>{r.reviews ?? "—"}</div>
                    <div className="col-span-1">
                      {r.website ? (
                        <a href={r.website} target="_blank" rel="noopener noreferrer"
                          className="text-xs hover:text-[#FF3B30] inline-flex items-center gap-1" style={{ color: "#6b7280" }}>
                          link <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : (
                        <div className="flex flex-col gap-1 items-start">
                          <span className="text-xs inline-flex items-center gap-1 font-mono" style={{ color: "#FF3B30" }}>
                            <AlertCircle className="w-3 h-3" /> none
                          </span>
                          <button
                            onClick={() => verifyCandidate(r)}
                            disabled={verifyingIdx !== null}
                            title="Check whether this business already has a live website"
                            className="text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1 disabled:opacity-40 hover:text-[#FF3B30] transition-colors"
                            style={{ color: "#9ca3af" }}
                          >
                            {verifyingIdx === candidateKey(r)
                              ? <><Loader2 className="w-2.5 h-2.5 animate-spin" /> checking</>
                              : <><Search className="w-2.5 h-2.5" /> verify</>}
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="col-span-1 text-right flex flex-col gap-1 items-end">
                      {!r.has_website && r.candidate_score >= MINIMUM_BUILD_SCORE ? (
                        <button
                          data-testid={`build-demo-${i}`}
                          onClick={() => startBuild(r, i)}
                          disabled={buildingFor !== null || candidateKey(reviewDemo?.candidate || {}) === candidateKey(r)}
                          className="px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.2em] inline-flex items-center gap-1 disabled:opacity-40 transition-colors hover:bg-[#E6352B]"
                          style={{ backgroundColor: "#FF3B30", color: "#ffffff" }}
                        >
                          <Sparkles className="w-3 h-3" /> BUILD
                        </button>
                      ) : null}
                      {(() => {
                        const pKey = `${r.title}|${r.address || ""}`;
                        const added = addedToPipeline.has(pKey);
                        return (
                          <button
                            onClick={async () => {
                              if (added) return;
                              try {
                                await createProspect({
                                  name: r.title, trade: r.trade, borough: session.borough,
                                  address: r.address, phone: r.phone, rating: r.rating,
                                  reviews: r.reviews, website: r.website,
                                  stage: "found",
                                });
                                setAddedToPipeline(prev => new Set([...prev, pKey]));
                                toast.success("Added to pipeline");
                              } catch { toast.error("Failed to add"); }
                            }}
                            title={added ? "In pipeline" : "Add to pipeline"}
                            className="px-2 py-1.5 text-[10px] font-mono uppercase tracking-[0.2em] inline-flex items-center gap-1 transition-colors"
                            style={added
                              ? { border: "1px solid #e5e7eb", color: "#d1d5db", cursor: "default" }
                              : { border: "1px solid #e5e7eb", color: "#6b7280" }
                            }
                          >
                            {added ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                          </button>
                        );
                      })()}
                    </div>
                  </div>
                );
              })}
              {filtered.length === 0 && (
                <div className="px-6 py-10 text-center font-mono text-sm" style={{ color: "#9ca3af" }}>
                  No matches with current filter.
                </div>
              )}
            </div>

            {/* Results — mobile cards */}
            <div className="md:hidden space-y-3">
              {filtered.map((r, i) => {
                const rowKey = `${r.title}-${r.address || ""}-${r.phone || ""}`;
                const pKey = `${r.title}|${r.address || ""}`;
                const added = addedToPipeline.has(pKey);
                return (
                  <div key={rowKey} data-testid={`bulk-result-card-${i}`}
                    style={{ backgroundColor: "#ffffff", border: "1px solid #e5e7eb" }}
                    className="rounded p-4">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div>
                        <div className="font-display text-base leading-tight mb-0.5" style={{ color: "#111827" }}>{r.title}</div>
                        <div className="font-mono text-[10px] uppercase tracking-wider" style={{ color: "#9ca3af" }}>{r.trade}</div>
                      </div>
                      <ScoreBadge score={r.candidate_score} />
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mb-3 font-mono text-[11px]" style={{ color: "#6b7280" }}>
                      {r.rating && <span>{r.rating}★ · {r.reviews} reviews</span>}
                      {r.address && <span>{r.address}</span>}
                      {r.phone && <a href={`tel:${r.phone}`} className="hover:text-[#FF3B30]">{r.phone}</a>}
                      {r.website ? (
                        <a href={r.website} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 hover:text-[#FF3B30]">
                          site <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : (
                        <>
                          <span className="inline-flex items-center gap-1" style={{ color: "#FF3B30" }}>
                            <AlertCircle className="w-3 h-3" /> no site
                          </span>
                          <button
                            onClick={() => verifyCandidate(r)}
                            disabled={verifyingIdx !== null}
                            className="inline-flex items-center gap-1 disabled:opacity-40 hover:text-[#FF3B30]"
                          >
                            {verifyingIdx === candidateKey(r)
                              ? <><Loader2 className="w-3 h-3 animate-spin" /> checking</>
                              : <><Search className="w-3 h-3" /> verify</>}
                          </button>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {!r.has_website && r.candidate_score >= MINIMUM_BUILD_SCORE && (
                        <button
                          data-testid={`build-demo-${i}`}
                          onClick={() => startBuild(r, i)}
                          disabled={buildingFor !== null || candidateKey(reviewDemo?.candidate || {}) === candidateKey(r)}
                          className="flex-1 py-2.5 text-[11px] font-mono uppercase tracking-[0.2em] inline-flex items-center justify-center gap-1.5 disabled:opacity-40 transition-colors"
                          style={{ backgroundColor: "#FF3B30", color: "#ffffff" }}>
                          {buildingFor?.idx === i
                            ? <><Loader2 className="w-3 h-3 animate-spin" /> Building…</>
                            : <><Sparkles className="w-3 h-3" /> Build demo</>}
                        </button>
                      )}
                      <button
                        onClick={async () => {
                          if (added) return;
                          try {
                            await createProspect({
                              name: r.title, trade: r.trade, borough: session.borough,
                              address: r.address, phone: r.phone, rating: r.rating,
                              reviews: r.reviews, website: r.website, stage: "found",
                            });
                            setAddedToPipeline(prev => new Set([...prev, pKey]));
                            toast.success("Added to pipeline");
                          } catch { toast.error("Failed to add"); }
                        }}
                        className="flex-1 py-2.5 text-[11px] font-mono uppercase tracking-[0.2em] inline-flex items-center justify-center gap-1.5 transition-colors"
                        style={added
                          ? { border: "1px solid #e5e7eb", color: "#d1d5db", cursor: "default" }
                          : { border: "1px solid #e5e7eb", color: "#6b7280" }}>
                        {added ? <><Check className="w-3 h-3" /> In pipeline</> : <><Plus className="w-3 h-3" /> Add to pipeline</>}
                      </button>
                    </div>
                  </div>
                );
              })}
              {filtered.length === 0 && (
                <div className="p-10 text-center font-mono text-sm rounded" style={{ border: "1px solid #e5e7eb", color: "#9ca3af", backgroundColor: "#ffffff" }}>
                  No matches with current filter.
                </div>
              )}
            </div>
          </>
        )}
      </section>

      {/* Scheduled jobs */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="flex items-end justify-between mb-6">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] mb-3" style={{ color: "#FF3B30" }}>OVERNIGHT JOBS</p>
            <h2 className="font-display text-3xl md:text-4xl tracking-tight">Wake up to fresh leads.</h2>
            <p className="mt-2 text-sm" style={{ color: "#6b7280" }}>APScheduler re-runs each saved query daily at the chosen UTC hour.</p>
          </div>
        </div>
        {jobs.length === 0 ? (
          <div className="p-10 text-center font-mono text-sm" style={{ border: "1px solid #e5e7eb", color: "#9ca3af", backgroundColor: "#ffffff" }}>
            No scheduled jobs yet.
          </div>
        ) : (
          <div style={{ border: "1px solid #e5e7eb" }}>
            {/* Desktop header */}
            <div
              className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 font-mono text-[10px] uppercase tracking-[0.2em]"
              style={{ borderBottom: "1px solid #e5e7eb", backgroundColor: "#ffffff", color: "#9ca3af" }}
            >
              <div className="col-span-3">JOB</div>
              <div className="col-span-2">BOROUGH</div>
              <div className="col-span-3">TRADES</div>
              <div className="col-span-1">UTC</div>
              <div className="col-span-2">LAST RUN</div>
              <div className="col-span-1 text-right">ACTIONS</div>
            </div>
            {jobs.map((j, i) => (
              <div
                key={j.id}
                data-testid={BULK.jobRow(i)}
                style={{ borderBottom: "1px solid #f3f4f6", backgroundColor: "#ffffff" }}
              >
                {/* Desktop row */}
                <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 items-center">
                  <div className="col-span-3 font-display text-base truncate">{j.name}</div>
                  <div className="col-span-2 text-sm" style={{ color: "#6b7280" }}>{j.borough}</div>
                  <div className="col-span-3 font-mono text-[10px] truncate" style={{ color: "#9ca3af" }}>{j.trades.join(" · ")}</div>
                  <div className="col-span-1 font-mono text-sm">{String(j.hour_utc).padStart(2, "0")}:00</div>
                  <div className="col-span-2 font-mono text-[10px]" style={{ color: "#6b7280" }}>
                    {j.last_run_at ? `${j.last_total ?? "?"} cand · ${new Date(j.last_run_at).toLocaleString()}` : "never"}
                    {j.last_session_id && (
                      <a href={bulkExportUrl(j.last_session_id)} className="block mt-1 hover:underline" style={{ color: "#FF3B30" }}>↓ CSV</a>
                    )}
                  </div>
                  <div className="col-span-1 flex justify-end gap-2">
                    <button data-testid={BULK.jobRunNow(i)} onClick={async () => {
                      toast("Running now…");
                      try { const r = await runLeadJobNow(j.id); toast.success(`${r.total} candidates`); refreshJobs(); }
                      catch { toast.error("Run failed"); }
                    }} title="Run now" className="p-2 hover:text-[#FF3B30] transition-colors" style={{ border: "1px solid #e5e7eb", color: "#6b7280" }}>
                      <Play className="w-3 h-3" />
                    </button>
                    <button data-testid={BULK.jobDelete(i)} onClick={async () => { await deleteLeadJob(j.id); toast.success("Job removed"); refreshJobs(); }}
                      title="Delete" className="p-2 hover:text-[#FF3B30] transition-colors" style={{ border: "1px solid #e5e7eb", color: "#6b7280" }}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
                {/* Mobile card */}
                <div className="md:hidden px-4 py-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div>
                      <div className="font-display text-base mb-0.5">{j.name}</div>
                      <div className="font-mono text-[10px] uppercase tracking-widest" style={{ color: "#9ca3af" }}>
                        {j.borough} · {String(j.hour_utc).padStart(2, "0")}:00 UTC
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button data-testid={`lead-job-run-now-mobile-${i}`} onClick={async () => {
                        toast("Running now…");
                        try { const r = await runLeadJobNow(j.id); toast.success(`${r.total} candidates`); refreshJobs(); }
                        catch { toast.error("Run failed"); }
                      }} className="p-2 hover:text-[#FF3B30]" style={{ border: "1px solid #e5e7eb", color: "#6b7280" }}>
                      </button>
                      <button data-testid={`lead-job-delete-mobile-${i}`} onClick={async () => { await deleteLeadJob(j.id); toast.success("Removed"); refreshJobs(); }}
                        className="p-2 hover:text-[#FF3B30]" style={{ border: "1px solid #e5e7eb", color: "#6b7280" }}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  <div className="font-mono text-[10px]" style={{ color: "#9ca3af" }}>{j.trades.join(" · ")}</div>
                  {j.last_run_at && (
                    <div className="font-mono text-[10px] mt-1" style={{ color: "#6b7280" }}>
                      Last: {j.last_total ?? "?"} candidates · {new Date(j.last_run_at).toLocaleDateString()}
                      {j.last_session_id && (
                        <a href={bulkExportUrl(j.last_session_id)} className="ml-2 hover:underline" style={{ color: "#FF3B30" }}>↓ CSV</a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Schedule modal */}
      {showSchedule && session && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }}
          onClick={() => setShowSchedule(false)}
        >
          <div
            className="max-w-lg w-full p-8"
            style={{ backgroundColor: "#ffffff", border: "1px solid #e5e7eb" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] mb-2" style={{ color: "#FF3B30" }}>SCHEDULE</p>
                <h3 className="font-display text-3xl tracking-tighter">Run this trawl every night.</h3>
              </div>
              <button onClick={() => setShowSchedule(false)} style={{ color: "#9ca3af" }}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-5">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] block mb-2" style={{ color: "#9ca3af" }}>JOB NAME</label>
                <input
                  data-testid={BULK.scheduleNameInput}
                  value={scheduleName}
                  onChange={(e) => setScheduleName(e.target.value)}
                  className="w-full px-3 py-3 outline-none"
                  style={{ backgroundColor: "#f9fafb", border: "1px solid #e5e7eb", color: "#111827" }}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] block mb-2" style={{ color: "#9ca3af" }}>BOROUGH</label>
                  <div className="font-display text-lg">{session.borough}</div>
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] block mb-2" style={{ color: "#9ca3af" }}>RUN AT (UTC)</label>
                  <input
                    data-testid={BULK.scheduleHourInput}
                    type="number"
                    min="0" max="23"
                    value={scheduleHour}
                    onChange={(e) => setScheduleHour(parseInt(e.target.value || "0", 10))}
                    className="w-full px-3 py-3 outline-none"
                    style={{ backgroundColor: "#f9fafb", border: "1px solid #e5e7eb", color: "#111827" }}
                  />
                </div>
              </div>
              <div className="font-mono text-[10px]" style={{ color: "#9ca3af" }}>
                TRADES: {session.trades.join(" · ")}
              </div>
            </div>
            <div className="mt-8 flex justify-end gap-3">
              <button
                onClick={() => setShowSchedule(false)}
                className="px-5 py-3 font-display transition-colors"
                style={{ border: "1px solid #e5e7eb", color: "#374151" }}
              >
                Cancel
              </button>
              <button
                data-testid={BULK.scheduleSubmit}
                onClick={async () => {
                  if (!scheduleName.trim()) { toast.error("Name the job"); return; }
                  try {
                    await createLeadJob({
                      name: scheduleName.trim(),
                      borough: session.borough,
                      trades: session.trades,
                      hour_utc: scheduleHour,
                    });
                    toast.success("Scheduled · runs daily");
                    setShowSchedule(false);
                    refreshJobs();
                  } catch { toast.error("Failed to schedule"); }
                }}
                className="bg-[#FF3B30] text-white px-6 py-3 font-display font-bold hover:bg-[#E6352B] transition-colors"
              >
                Schedule daily →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Brief review — gate before the expensive full site build */}
      {reviewDemo && (
        <BriefReviewModal
          demo={reviewDemo.demo}
          businessName={reviewDemo.candidate.title}
          contact={reviewDemo.candidate}
          onApproved={handleBriefApproved}
          onClose={() => setReviewDemo(null)}
        />
      )}

      {/* Build-in-progress overlay */}
      {buildingFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: "rgba(0,0,0,0.92)" }}>
          <div className="text-center max-w-md px-8">
            <div className="flex justify-center gap-2 mb-10">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: "#FF3B30", animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite` }}
                />
              ))}
            </div>
            <div className="font-display text-3xl md:text-4xl tracking-tighter mb-4 text-white">{buildingFor.name}</div>
            <div className="font-mono text-[11px] uppercase tracking-[0.2em]" style={{ color: "rgba(255,255,255,0.4)" }}>
              Pulling live reviews · drafting the brief for review
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

const Stat = ({ v, l, accent }) => (
  <div>
    <div className="font-display text-3xl font-bold" style={{ color: accent ? "#FF3B30" : "#111827" }}>{v}</div>
    <div className="font-mono text-[10px] uppercase tracking-[0.2em] mt-1" style={{ color: "#9ca3af" }}>{l}</div>
  </div>
);

const HIGH_SCORE_THRESHOLD = 75;
const MEDIUM_SCORE_THRESHOLD = 50;

const ScoreBadge = ({ score }) => {
  let bg, color;
  if (score >= HIGH_SCORE_THRESHOLD) {
    bg = "#FF3B30"; color = "#ffffff";
  } else if (score >= MEDIUM_SCORE_THRESHOLD) {
    bg = "#ffeeed"; color = "#FF3B30";
  } else {
    bg = "#f3f4f6"; color = "#6b7280";
  }
  return (
    <div className="inline-block px-2 py-1 font-mono text-xs font-bold" style={{ backgroundColor: bg, color }}>
      {score}
    </div>
  );
};
